import ExtendedWarrantyService from "@/services/extendedWarrantyService";
import { subDays } from "date-fns";

import { getMerchantId } from "@/lib/authStorage";
import { ALL_SALES_FILTER, SALE_STATUS_FILTERS } from "@/lib/saleStatus";

import i18n from "@/i18n";

import { create } from "zustand";

const initialPlanFilters = {
  page: 1,
  size: localStorage.getItem("pageSize") || 10,
  status: null,
  name: "",
  planType: null,
  packageCode: "",
};

const initialTransactionFilters = {
  page: 1,
  size: localStorage.getItem("pageSize") || 10,
  firstName: "",
  lastName: "",
  // EPS-4974: ana merchant satışları alt bayi bazında filtreleyebilir.
  // Alt bayi (STORE) kullanıcılarda backend zaten kendi kayıtlarına daraltır.
  merchantUserId: "",
  // EPS-5027: listeyi tek bir alt bayinin (ayrı merchant) satışlarına daraltır.
  // merchantUserId ile aynı şey DEĞİL: o alan aynı merchant içindeki STORE
  // kullanıcıyı seçer, bu alan main_merchant_id ile bağlı ayrı bir merchant'ı.
  subMerchantId: "",
  // EPS-5115: statü filtresi, SALE_STATUS_FILTERS anahtarlarından biri. Varsayılan
  // "Tümü": taslaklar + aktif + iptal edilmiş satışlar birlikte. Seçimin hangi
  // kaynağa (taslak/satış) hangi parametreyle gittiği orada tanımlı.
  saleStatus: ALL_SALES_FILTER,
};

/**
 * Taslak kaydını satış listesi satırı şekline çevirir.
 *
 * Statü rozeti `status`/`activationCode`/`deleted` alanlarından türetiliyor; taslakta
 * bunların hiçbiri olmadığı için türetme doğal olarak "Taslak Satış" döner
 * (bkz. @/lib/saleStatus).
 */
const mapDraftToRow = (draft) => {
  const productInfo = draft.productSnapshot?.productInfo;
  const plan = draft.productSnapshot?.selectedPlan;
  const customer = draft.customerSnapshot || {};

  return {
    id: draft.id,
    isDraft: true,
    // EPS-5027: taslak her zaman oturumdaki merchant'ın kendi kaydıdır (alt bayi
    // seçiliyken taslak ucu hiç çağrılmıyor), BE bu alanı taşımıyor. "Alt Bayi"
    // kolonu taslak satırlarında "-" kalmasın diye oturumun merchant'ı yazılır.
    merchantId: Number(getMerchantId()) || null,
    firstName: customer.firstName || "",
    lastName: customer.lastName || "",
    // Ürün adı taslakta müşteri adımında giriliyor; henüz girilmemişse kategori
    // adı gösterilir (taslak plan seçildikten sonra kaydedildiği için hep var).
    productName:
      customer.productName ||
      productInfo?.categoryName ||
      productInfo?.mainCategoryName ||
      "-",
    externalProductName: plan?.name || "-",
    extendedWarrantyMonth: plan?.durationMonth || 0,
    extendedWarrantyYear: 0,
    createdAt: draft.updatedAt || draft.createdAt,
    // EPS-4971: taslağın ödeme durumu. Statü rozeti bunu görürse "Taslak Satış"
    // yerine "Ödeme Bekleniyor" / "Link Süresi Doldu" gösterilir.
    paymentStatus: draft.paymentStatus || null,
    paymentLinkExpiresAt: draft.paymentLinkExpiresAt || null,
    paymentLinkChannel: draft.paymentLinkChannel || null,
    // EPS-4971: "Bayi" kolonu. Ödeme linki gönderilen taslaklarda dolu; düz
    // taslaklarda BE bu bilgiyi taşımıyor ve kolon "-" kalıyor.
    storeCode: draft.storeCode || null,
    // EPS-5097: "Satışı Yapan Personel" formda girilen ad; EPS-5097 öncesi ödeme linki
    // taslaklarında yalnızca linki gönderen panel kullanıcısının adı var.
    personalName: customer.salesPersonnelName || draft.personalName || null,
  };
};

/**
 * Seçilen statünün kaynaklarına göre bir liste sayfası getirir (bkz. SALE_STATUS_FILTERS):
 * yalnızca taslak, yalnızca satış ya da ikisi birden.
 *
 * EPS-5027: taslaklar (seller_sale_draft) merchant'ın kendi kayıtları; başka bir
 * bayinin taslağı görünemez. Alt bayiye göre filtrelenmişken taslak ucu hiç
 * çağrılmaz, aksi halde seçilen bayiye ait olmayan taslaklar listeye karışırdı.
 */
const fetchSalesPage = async (query, { drafts, sales }) => {
  const draftQuery =
    drafts && !query.subMerchantId
      ? {
          page: query.page,
          size: query.size,
          merchantUserId: query.merchantUserId,
          firstName: query.firstName,
          lastName: query.lastName,
          ...drafts,
        }
      : null;
  const saleQuery = sales ? { ...query, ...sales } : null;

  if (draftQuery && saleQuery) return fetchMergedSalesPage(draftQuery, saleQuery);
  if (saleQuery) return ExtendedWarrantyService.getExtendedWarranties(saleQuery);
  if (!draftQuery) return { content: [], totalPages: 0, totalElements: 0 };

  const raw = await ExtendedWarrantyService.getSaleDrafts(draftQuery);
  return { ...raw, content: (raw.content || []).map(mapDraftToRow) };
};

/**
 * Taslak ve satış kaynakları birlikte istendiğinde ("Tümü", "Taslak Satış") liste iki
 * kaynaktan oluşur: taslaklar bff'teki seller_sale_draft tablosunda, satışlar
 * customer-api'de. Tek sorguda birleştirilemedikleri için sayfalama burada birleştiriliyor:
 *
 *   birleşik liste = [ taslaklar (updatedAt DESC) , satışlar (id DESC) ]
 *
 * Taslaklar listenin başında durduğu için birleşik sayfa P'nin taslak dilimi
 * doğrudan taslak sayfası P'ye denk gelir. Satış dilimi ise taslak sayısı (D)
 * kadar kayar; kayma sayfa sınırına oturmadığında dilim iki satış sayfasından
 * kırpılır — bu yüzden en fazla iki satış isteği yapılır.
 *
 * Taslaklar en üstte: yarım kalmış kayıt bayinin işlem yapması gereken satır ve
 * iki kaynağı tarihe göre gerçekten harmanlamak BE'de tek bir union sorgusu
 * gerektirir.
 */
const fetchMergedSalesPage = async (draftQuery, saleQuery) => {
  const size = Number(saleQuery.size) || 10;
  const page = Math.max(Number(saleQuery.page) || 1, 1);
  const offset = (page - 1) * size;

  // Taslak ucu erişilemezse (BE/gateway henüz deploy edilmemiş, 404/5xx) liste
  // satışlarla çalışmaya devam eder: "Tümü" panelin varsayılan görünümü, tek bir
  // uç yüzünden komple boş kalmamalı.
  const draftPage = await ExtendedWarrantyService.getSaleDrafts({
    ...draftQuery,
    page,
    size,
  }).catch((error) => {
    console.error("Taslaklar alınamadı, liste taslaksız gösteriliyor:", error);
    return null;
  });
  const draftRows = (draftPage?.content || []).map(mapDraftToRow);
  const draftTotal = draftPage?.totalElements || 0;

  // Bu sayfada gösterilecek satış dilimi: [saleStart, saleStart + saleCount)
  const saleStart = Math.max(0, offset - draftTotal);
  const saleCount = size - draftRows.length;
  const salePage = Math.floor(saleStart / size) + 1;
  const offsetInPage = saleStart % size;

  // saleCount 0 olsa bile (sayfa tamamen taslaklardan oluşuyor) istek atılıyor:
  // toplam sayfa sayısı satış toplamı olmadan hesaplanamaz.
  const firstSales = await ExtendedWarrantyService.getExtendedWarranties({
    ...saleQuery,
    page: salePage,
    size,
  });
  let saleRows = (firstSales?.content || []).slice(
    offsetInPage,
    offsetInPage + saleCount,
  );
  if (saleRows.length < saleCount && salePage < (firstSales?.totalPages || 0)) {
    const nextSales = await ExtendedWarrantyService.getExtendedWarranties({
      ...saleQuery,
      page: salePage + 1,
      size,
    });
    saleRows = [
      ...saleRows,
      ...(nextSales?.content || []).slice(0, saleCount - saleRows.length),
    ];
  }

  const totalElements = draftTotal + (firstSales?.totalElements || 0);

  return {
    content: [...draftRows, ...saleRows],
    totalElements,
    totalPages: Math.ceil(totalElements / size),
    // İstenen sayfa: satış ucu 0-tabanlı `page` döndürdüğü için yanıttan okunamaz.
    page,
  };
};

const initialSaleReportFilters = {
  dateRange: {
    from: subDays(new Date(), 6),
    to: new Date(),
  },
  category: null,
  store: null,
  plan: null,
};

const useExtendedWarrantyStore = create((set, get) => ({
  loading: false,
  // Transaction related state
  extendedWarranties: [],
  filters: { ...initialTransactionFilters },
  // Package related state
  additionalServicePlans: [],
  currentPlan: null,
  planFilters: { ...initialPlanFilters },
  pagination: {
    totalPages: 0,
    totalElements: 0,
    currentPage: 1,
  },
  // Sale reports related state
  saleReports: {
    totalSales: 0,
    totalRevenue: 0,
    averagePackagePrice: 0,
    activeStores: 0,
    categories: [],
    stores: [],
    packages: [],
    salesTrend: [],
    categoryDistribution: [],
    packageDistribution: [],
    storeRanking: [],
    personnelPerformance: [],
  },
  saleReportFilters: { ...initialSaleReportFilters },
  filterData: {
    stores: [],
    packageTypes: [],
    categories: [],
    loading: false,
  },

  // Transaction related methods
  getExtendedWarranties: async () => {
    set({ loading: true });
    try {
      const { saleStatus, ...query } = get().filters;
      const data = await fetchSalesPage(
        query,
        SALE_STATUS_FILTERS[saleStatus] || SALE_STATUS_FILTERS[ALL_SALES_FILTER],
      );

      set({
        extendedWarranties: data.content,
        filters: {
          ...get().filters,
          totalPages: data.totalPages,
          totalElements: data.totalElements,
          // İstenen sayfa korunuyor: satış ucu `page`i 0-tabanlı döndürüyor
          // (customer-api PagingResult → Page#getNumber), taslak ucu 1-tabanlı.
          // Yanıttan okumak sayfalama göstergesini bir sayfa kaydırıyordu.
          page: query.page,
        },
        loading: false,
      });
      return data;
    } catch (error) {
      console.error("Error fetching extended warranties:", error);
      set({ loading: false });
      throw error;
    }
  },

  setFilters: (newFilters) => {
    set({ filters: { ...get().filters, ...newFilters } });
  },

  // Package related methods
  updatePlanFilters: async (newFilters) => {
    const updatedFilters = {
      ...get().planFilters,
      ...newFilters,
    };

    set({ planFilters: updatedFilters });
    return get().getAdditionalServicePlans();
  },

  handlePageChange: async (page) => {
    return get().updatePlanFilters({ page });
  },

  handlePageSizeChange: async (size) => {
    localStorage.setItem("pageSize", size);
    return get().updatePlanFilters({ size, page: 1 });
  },

  getAdditionalServicePlans: async () => {
    set({ loading: true });
    try {
      const data = await ExtendedWarrantyService.getAdditionalServicePlans(
        get().planFilters,
      );

      set({
        additionalServicePlans: data.content,
        pagination: {
          totalPages: data.totalPages,
          totalElements: data.totalElements,
          currentPage: data.page,
        },
        loading: false,
      });

      return data;
    } catch (error) {
      console.error("Error fetching service plans:", error);
      set({ loading: false });
      throw error;
    }
  },

  getAdditionalServicePlan: async (id) => {
    set({ loading: true });
    try {
      const data = await ExtendedWarrantyService.getAdditionalServicePlan(id);
      set({ currentPlan: data, loading: false });
      return data;
    } catch (error) {
      console.error("Error fetching service plan:", error);
      set({ loading: false });
      throw error;
    }
  },

  createAdditionalServicePlan: async (planData) => {
    set({ loading: true });
    try {
      const data =
        await ExtendedWarrantyService.createAdditionalServicePlan(planData);
      set({ loading: false });
      return data;
    } catch (error) {
      console.error("Error creating service plan:", error);
      set({ loading: false });
      throw error;
    }
  },
  updateAdditionalServicePlan: async (id, planData) => {
    set({ loading: true });
    try {
      const data = await ExtendedWarrantyService.updateAdditionalServicePlan(
        id,
        planData,
      );
      set({ loading: false });
      return data;
    } catch (error) {
      console.error("Error updating service plan:", error);
      set({ loading: false });
      throw error;
    }
  },
  deleteAdditionalServicePlan: async (id) => {
    set({ loading: true });
    try {
      await ExtendedWarrantyService.deleteAdditionalServicePlan(id);
      set({ loading: false });
      return get().getAdditionalServicePlans();
    } catch (error) {
      console.error("Error deleting service plan:", error);
      set({ loading: false });
      throw error;
    }
  },

  resetPlanFilters: () => {
    set({ planFilters: { ...initialPlanFilters } });
    return get().getAdditionalServicePlans();
  },

  getSaleReports: async () => {
    set({ loading: true });
    try {
      const data = await ExtendedWarrantyService.getSaleReports(
        get().getSaleReportsPayload(),
      );
      set({
        saleReports: {
          totalSales: data.totalSales || 0,
          totalRevenue: data.totalRevenue || 0,
          averagePackagePrice: data.averagePackagePrice || 0,
          activeStores: data.activeStores || 0,
          categories: data.categories || [],
          stores: data.stores || [],
          packages: data.packages || [],
          salesTrend: data.salesTrend || [],
          categoryDistribution: data.categoryDistribution || [],
          packageDistribution: data.packageDistribution || [],
          storeRanking: data.storeRanking || [],
          personnelPerformance: data.personnelPerformance || [],
        },
        filterData: {
          stores: [
            { label: i18n.t("extendedWarranty.reports.filters.allStores", "Tüm Mağazalar"), value: "all" },
            ...(data.stores || []).map((store) => ({
              label: store,
              value: store,
            }))
          ],
          packageTypes: [
            { label: i18n.t("extendedWarranty.reports.filters.allPackages", "Tüm Paketler"), value: "all" },
            ...(data.packages || []).map((pkg) => ({
              label: pkg,
              value: pkg,
            }))
          ],
          categories: [
            { label: i18n.t("extendedWarranty.reports.filters.allCategories", "Tüm Kategoriler"), value: "all" },
            ...(data.categories || []).map((cat) => ({
              label: cat,
              value: cat,
            }))
          ],
          loading: false,
        },
        loading: false,
      });
      return data;
    } catch (error) {
      console.error("Error fetching sale reports:", error);
      set({ loading: false });
      throw error;
    }
  },

  updateSaleReportFilters: async (newFilters) => {
    const updatedFilters = {
      ...get().saleReportFilters,
      ...newFilters,
    };
    set({ saleReportFilters: updatedFilters });
    return get().getSaleReports();
  },

  getSaleReportsPayload: () => {
    const filters = get().saleReportFilters;
    return {
      fromDate: filters.dateRange?.from
        ? new Date(filters.dateRange.from).toISOString().split("T")[0]
        : "",
      toDate: filters.dateRange?.to
        ? new Date(filters.dateRange.to).toISOString().split("T")[0]
        : "",
      category: filters.category || "",
      store: filters.store || "",
      plan: filters.plan || "",
    };
  },

  exportSaleReports: async (format = "excel") => {
    try {
      const blob = await ExtendedWarrantyService.exportSaleReports(format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sale-reports.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      return true;
    } catch (error) {
      console.error("Error exporting sale reports:", error);
      throw error;
    }
  },

  getFilterData: async () => {
    if (get().filterData.stores.length > 0) {
      return get().filterData;
    }
    return get().getSaleReports();
  },

  resetSaleReportFilters: async () => {
    set({ saleReportFilters: { ...initialSaleReportFilters } });
    return get().getSaleReports();
  },
}));

export default useExtendedWarrantyStore;
